import { AppPhase } from '../types';
import type { ScanFlowStep } from './scanGrading/types';
import { isAppShellPhase } from './appShellMode';

/**
 * App bottom-nav visibility + safe-leave rules for the new app shell.
 *
 * Pure, React-free, and behind {@link APP_SHELL_ENABLED} at the call site so it
 * never changes default production behavior when `VITE_APP_SHELL` is off.
 *
 * The nav stays visible across the whole grading workflow (Setup → class →
 * assignment → rubric → ready-to-scan → review → insights → final actions) and
 * hides ONLY on the active capture surface (the live camera step or the
 * full-screen manual crop editor), where it would overlap capture controls.
 */

/**
 * Focused-workflow steps that own a full-screen capture surface. The app bottom
 * nav must hide here because it would overlap the camera/capture controls.
 *
 * Only the live `camera` step qualifies. `scan_start` (ready-to-scan),
 * `match_review`, `processing`, `review`, and every later step keep the nav.
 */
export const CAPTURE_SURFACE_STEPS: ReadonlySet<ScanFlowStep> = new Set<ScanFlowStep>(['camera']);

/**
 * Approximate rendered height of {@link AppBottomNav} (icon + label + padding).
 * Used by the grading workflow to lift its sticky action bar above the nav and
 * to reserve scroll padding so controls are never covered. Includes the iOS
 * home-indicator safe area.
 */
export const APP_BOTTOM_NAV_OFFSET = 'calc(3.25rem + env(safe-area-inset-bottom))';

export interface AppBottomNavVisibilityInput {
  /** {@link APP_SHELL_ENABLED}. */
  appShellEnabled: boolean;
  /** Firebase auth verified / signed in. */
  isSignedIn: boolean;
  /** Current app phase. */
  phase: AppPhase;
  /**
   * Current focused-workflow step when `phase === SCAN_WORKFLOW`. Ignored for
   * every other phase. `null`/`undefined` is treated as "not on a capture
   * surface".
   */
  scanStep?: ScanFlowStep | null;
  /** True when the full-screen manual crop editor is open. */
  cropEditorOpen?: boolean;
}

/**
 * True when a full-screen capture surface (live camera or the crop editor) is
 * active and the bottom nav must be hidden.
 */
export function isCaptureSurfaceActive(
  scanStep?: ScanFlowStep | null,
  cropEditorOpen?: boolean
): boolean {
  if (cropEditorOpen) return true;
  return !!scanStep && CAPTURE_SURFACE_STEPS.has(scanStep);
}

/**
 * Single source of truth for whether the app bottom nav should render.
 *
 * Returns true for every app-shell phase, and for `SCAN_WORKFLOW` as long as
 * the teacher is NOT on the live camera / crop capture surface. Returns false
 * when the flag is off, when signed out, and for all legacy/dormant phases.
 */
export function shouldShowAppBottomNav(input: AppBottomNavVisibilityInput): boolean {
  const { appShellEnabled, isSignedIn, phase, scanStep, cropEditorOpen } = input;
  if (!appShellEnabled || !isSignedIn) return false;
  if (isAppShellPhase(phase)) return true;
  // Privacy 6B: the Privacy Dashboard is a Manage sub-screen — keep the nav
  // visible (with Manage active) so it feels like part of the shell.
  if (phase === AppPhase.PRIVACY_DASHBOARD || phase === AppPhase.COMPLIANCE_STATUS) return true;
  if (phase === AppPhase.SCAN_WORKFLOW) {
    return !isCaptureSurfaceActive(scanStep, cropEditorOpen);
  }
  return false;
}

/** Teacher-facing confirmation shown before leaving grading with active work. */
export const LEAVE_GRADING_CONFIRM_MESSAGE = 'Leave grading? Your current work may not be saved.';

export interface LeaveGradingGuardInput {
  /** Current app phase. */
  phase: AppPhase;
  /** True when a scan session is in progress (capture/review work could be lost). */
  sessionActive: boolean;
}

/**
 * True when tapping an app-shell tab should first confirm with the teacher.
 *
 * Leaving `SCAN_WORKFLOW` unmounts the focused workflow and resets the in-memory
 * scan session, so an in-progress session would be lost. We only guard once a
 * session actually exists; the early setup steps (class/assignment/rubric, before
 * any capture) have nothing to lose and navigate immediately.
 */
export function shouldConfirmLeaveGrading(input: LeaveGradingGuardInput): boolean {
  return input.phase === AppPhase.SCAN_WORKFLOW && input.sessionActive;
}
